import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiBase";

const AuthContext = createContext(null);
const RETURN_TO_STORAGE_KEY = "dp_return_to";

// --- Helpers -------------------------------------------------
const isProbablyDataUrl = (v) => typeof v === "string" && v.startsWith("data:");
const clampString = (v, max = 2048) => (typeof v === "string" ? v.slice(0, max) : v);
const normalizeReturnToPath = (value) => {
  if (typeof value !== "string" || !value.startsWith("/")) return "";

  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return "";
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "";
  }
};

const persistReturnTo = (value) => {
  if (typeof window === "undefined") return "";
  const normalized = normalizeReturnToPath(value);

  if (normalized) {
    sessionStorage.setItem(RETURN_TO_STORAGE_KEY, normalized);
  } else {
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  }

  return normalized;
};

const normalizeUser = (u) => {
  if (!u) return null;

  const meta = u.user_metadata || {};
  const identities = Array.isArray(u.identities) ? u.identities : [];
  const id0 = identities[0]?.identity_data || {};
  const email = u.email || id0.email || "";

  const name =
    u.name ||
    meta.full_name ||
    meta.name ||
    meta.display_name ||
    id0.name ||
    id0.full_name ||
    (email ? email.split("@")[0] : "");

  // IMPORTANT: never allow gigantic base64 images to flow into UI state.
  // Prefer OAuth picture URLs; if a data: URL got stored in metadata, ignore it.
  const rawPicture =
    u.avatar_url ||
    u.picture ||
    meta.avatar_url ||
    meta.picture ||
    id0.picture ||
    id0.avatar_url ||
    "";

  const avatar_url = isProbablyDataUrl(rawPicture) ? "" : clampString(rawPicture, 4096);
  const picture = avatar_url;

  const bio = u.bio ?? meta.bio ?? "";
  const focus_preferences = Array.isArray(u.focus_preferences) 
    ? u.focus_preferences 
    : Array.isArray(meta.focus_preferences) 
    ? meta.focus_preferences 
    : Array.isArray(meta.focusPreferences) 
    ? meta.focusPreferences 
    : [];

  return {
    ...u,
    id: u.id ?? null,
    email,
    name,
    avatar_url,
    picture,
    bio,
    focus_preferences,
  };
};

const mergeSessionAndBackendUser = (sessionUser, backendUser) => {
  const sessionNormalized = normalizeUser(sessionUser || null) || {};
  const backendNormalized = normalizeUser(backendUser || null) || {};

  return normalizeUser({
    ...backendNormalized,
    ...sessionNormalized,
    id: sessionNormalized.id || backendNormalized.id || null,
    email: sessionNormalized.email || backendNormalized.email || "",
    // Prefer backend only when it has a value; otherwise use session (Supabase/OAuth) so we don't overwrite with empty
    name: (backendNormalized.name && String(backendNormalized.name).trim()) ? backendNormalized.name : (sessionNormalized.name || ""),
    bio: (backendNormalized.bio && String(backendNormalized.bio).trim()) ? backendNormalized.bio : (sessionNormalized.bio || ""),
    focus_preferences:
      (backendNormalized.focus_preferences?.length && backendNormalized.focus_preferences) ||
      sessionNormalized.focus_preferences ||
      [],
    avatar_url: sessionNormalized.avatar_url || backendNormalized.avatar_url || "",
    picture: sessionNormalized.picture || backendNormalized.picture || "",
    user_metadata: sessionUser?.user_metadata || backendUser?.user_metadata || {},
  });
};

const mergeUserMetadataSafely = (currentMeta, updates) => {
  const merged = { ...(currentMeta || {}), ...(updates || {}) };

  // Hard guardrail: never persist profile images in Supabase user_metadata.
  // Profile images are stored in backend Mongo as public storage URLs only.
  ["picture", "avatar_url"].forEach((k) => {
    delete merged[k];
  });

  return merged;
};

// --- Provider ------------------------------------------------
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const isAuthenticated = !!session?.access_token;

  const isAdmin = useMemo(() => {
    const tier = (user?.tier || session?.user?.user_metadata?.tier || "").toLowerCase();
    return tier === "admin";
  }, [user?.tier, session?.user?.user_metadata?.tier]);

  const isTrialExpired = false;

  const fetchBackendUser = useCallback(async (accessToken = null) => {
    if (!accessToken) return null;

    try {
      const res = await apiFetch("/users/me", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (res.status === 401) return null;
      if (!res.ok) {
        console.warn("fetchBackendUser non-OK status:", res.status);
        return null;
      }
      return await res.json();
    } catch (e) {
      console.warn("fetchBackendUser error:", e);
      return null;
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data?.session?.access_token || null;
    } catch (e) {
      console.error("getAccessToken error:", e);
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      const s = data?.session ?? null;
      setSession(s);
      const backendUser = await fetchBackendUser(s?.access_token);
      setUser(
        backendUser
          ? mergeSessionAndBackendUser(s?.user ?? null, backendUser)
          : normalizeUser(s?.user ?? null)
      );
      return !!s;
    } catch (e) {
      console.error("checkAuth error:", e);
      setSession(null);
      setUser(null);
      return false;
    } finally {
      setAuthLoading(false);
    }
  }, [fetchBackendUser]);

  const loginWithPassword = useCallback(async (email, password) => {
    const cleanEmail = (email || "").trim().toLowerCase();
    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password: password || "",
    });
    if (error) throw error;
    return data;
  }, []);

  const register = useCallback(async (email, password, name = '', refSource = null, returnTo = "") => {
    const cleanEmail = (email || "").trim().toLowerCase();
    const metadata = { name };
    const normalizedReturnTo = persistReturnTo(returnTo);
    const redirectTo = new URL("/auth/callback", window.location.origin);
    if (normalizedReturnTo) {
      redirectTo.searchParams.set("returnTo", normalizedReturnTo);
    }
    if (refSource) {
      metadata.ref_source = refSource;
    }
    const { data, error} = await supabase.auth.signUp({
      email: cleanEmail,
      password: password || "",
      options: {
        emailRedirectTo: redirectTo.toString(),
        data: mergeUserMetadataSafely({}, metadata),
      },
    });
    if (error) throw error;
    return data;
  }, []);

  const updateUser = useCallback(async (updates = {}) => {
    try {
      const { data: userRes, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;

      const currentUser = userRes?.user || null;
      const currentMeta = currentUser?.user_metadata || {};

      const mergedMeta = mergeUserMetadataSafely(currentMeta, updates);

      const { data, error } = await supabase.auth.updateUser({ data: mergedMeta });
      if (error) throw error;

      const { data: fresh } = await supabase.auth.getSession();
      setSession(fresh?.session ?? null);
      const backendUser = await fetchBackendUser(fresh?.session?.access_token);
      setUser(
        backendUser
          ? mergeSessionAndBackendUser(fresh?.session?.user ?? null, backendUser)
          : normalizeUser(fresh?.session?.user ?? null)
      );

      return data;
    } catch (e) {
      console.error("updateUser error:", e);
      throw e;
    }
  }, [fetchBackendUser]);

  const setUserFromBackend = useCallback((backendUser = {}) => {
    setUser(mergeSessionAndBackendUser(session?.user ?? null, backendUser || {}));
  }, [session?.user]);

  const withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error("signOut timeout")), ms)),
    ]);

  const logout = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("[Auth] logout() started");
      }

      try {
        await withTimeout(supabase.auth.signOut(), 3000);
        if (process.env.NODE_ENV === "development") {
          console.log("[Auth] supabase.signOut resolved");
        }
      } catch (e) {
        console.warn("[Auth] supabase.signOut did not complete:", e);
      }
    } finally {
      setSession(null);
      setUser(null);

      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith("sb-") || k.includes("code-verifier") || k.toLowerCase().includes("supabase"))
          .forEach((k) => localStorage.removeItem(k));
      } catch (e) {
        console.warn("[Auth] localStorage cleanup error:", e);
      }

      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith("sb-") || k.includes("code-verifier") || k.toLowerCase().includes("supabase"))
          .forEach((k) => localStorage.removeItem(k));
      } catch (e) {
        console.warn("[Auth] localStorage cleanup error (pass2):", e);
      }

      if (process.env.NODE_ENV === "development") {
        console.log(
          "[Auth] logout cleanup complete",
          Object.keys(localStorage).filter((k) => k.startsWith("sb-"))
        );
        console.log(
          "[Auth] logout cleanup complete (post-removal)",
          Object.keys(localStorage).filter((k) => k.startsWith("sb-"))
        );
      }

      sessionStorage.setItem("dp_force_logout", "1");
      setTimeout(() => window.location.replace("/login"), 200);
    }
  }, []);

  // --- Init + subscribe --------------------------------------
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const force = typeof window !== "undefined" && sessionStorage.getItem("dp_force_logout") === "1";
        if (force) {
          sessionStorage.removeItem("dp_force_logout");
          try {
            // best-effort signOut again
            await Promise.race([
              supabase.auth.signOut(),
              new Promise((resolve) => setTimeout(resolve, 1500)),
            ]);
          } catch {}
          try {
            Object.keys(localStorage)
              .filter((k) => k.startsWith("sb-") || k.includes("code-verifier") || k.toLowerCase().includes("supabase"))
              .forEach((k) => localStorage.removeItem(k));
          } catch {}
          setSession(null);
          setUser(null);
          if (process.env.NODE_ENV === "development") {
            console.log("[Auth] force-logout scrub ran");
          }
        }

        const { data, error } = await supabase.auth.getSession();
        if (error) console.error("getSession error:", error);
        if (!mounted) return;
        const s = data?.session ?? null;
        setSession(s);
        const backendUser = await fetchBackendUser(s?.access_token);
        if (!mounted) return;
        setUser(
          backendUser
            ? mergeSessionAndBackendUser(s?.user ?? null, backendUser)
            : normalizeUser(s?.user ?? null)
        );
      } catch (e) {
        console.error("Auth init error:", e);
        if (!mounted) return;
        setSession(null);
        setUser(null);
      } finally {
        if (mounted) setAuthLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      if (!mounted) return;
      setSession(newSession ?? null);
      const backendUser = await fetchBackendUser(newSession?.access_token);
      if (!mounted) return;
      setUser(
        backendUser
          ? mergeSessionAndBackendUser(newSession?.user ?? null, backendUser)
          : normalizeUser(newSession?.user ?? null)
      );
      setAuthLoading(false);
    });

    const t = setTimeout(() => {
      if (mounted) setAuthLoading(false);
    }, 5000);

    return () => {
      mounted = false;
      clearTimeout(t);
      sub?.subscription?.unsubscribe();
    };
  }, [fetchBackendUser]);

  const value = useMemo(
    () => ({
      // state
      session,
      user,

      // loading flags (both names, because the codebase uses both)
      authLoading,
      loading: authLoading,

      // flags
      isAuthenticated,
      isAdmin,
      isTrialExpired,

      // functions
      getAccessToken,
      checkAuth,
      updateUser,
      setUserFromBackend,

      // login/register variants
      loginWithPassword,
      register,

      // logout
      logout,
    }),
    [
      session,
      user,
      authLoading,
      isAuthenticated,
      isAdmin,
      isTrialExpired,
      getAccessToken,
      checkAuth,
      updateUser,
      setUserFromBackend,
      loginWithPassword,
      register,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
